import { Injectable } from '@angular/core';

import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { BetslipListType, MyBetsListSlimResponse as MyBetsListSlimResponseDto } from '@frontend/sports/types/models/my-bets';
import { flatten } from 'lodash-es';
import { Observable, catchError, map, of, switchMap } from 'rxjs';

import {
    MyBetsFilteredResponse,
    MyBetsListSlimResponse,
    MyBetsSingleResponse,
    myBetsFilteredErrorResponse,
    myBetsListSlimErrorResponse,
    myBetsSingleErrorResponse,
} from '../../my-bets-base/models/my-bets-response';
import { MyBetsSummaryService } from '../../my-bets-base/my-bets-summary.service';
import { IMyBetsListRequest, MyBetsApiService } from './my-bets-api.service';
import { MyBetsFixtureDataService } from './my-bets-fixture-data.service';

@Injectable({ providedIn: 'root' })
export class MyBetsLoaderService {
    constructor(
        private api: MyBetsApiService,
        private summaryService: MyBetsSummaryService,
        private fixtureData: MyBetsFixtureDataService,
        protected trackingService: TrackingService,
    ) {}

    async getEditBetBetslips(betslipId: string, associatedAccount: Nullable<string>): Promise<MyBetsListSlimResponseDto> {
        return await this.api.getEditBetBetslips(betslipId, associatedAccount);
    }

    getSingleBetslip(betslipId: string, associatedAccount: string): Observable<MyBetsSingleResponse> {
        return this.api.getSingleBetslip({ betslipId, associatedAccount }).pipe(
            map((response) => response ?? myBetsSingleErrorResponse()),
            switchMap((response) =>
                this.fixtureData.fetch([response.betslip!], true).pipe(
                    catchError(() => of({})),
                    map((events) => ({
                        betslip: response.betslip ?? null,
                        events,
                    })),
                ),
            ),
            catchError(() => of(myBetsSingleErrorResponse())),
        );
    }

    getMyBets(options: IMyBetsListRequest): Observable<MyBetsFilteredResponse> {
        return this.api.getMyBets(options).pipe(
            map((response) => response ?? myBetsFilteredErrorResponse()),
            switchMap((betslipResponse) =>
                this.fixtureData.fetch(betslipResponse.betslips, betslipResponse.typeFilter === BetslipListType.Settled).pipe(
                    catchError(() => of({})),
                    map((events) => {
                        const response: MyBetsFilteredResponse = {
                            events,
                            betslips: betslipResponse.betslips,
                            summary: betslipResponse.summary,
                            typeFilter: betslipResponse.typeFilter,
                            errorLoadingBets: betslipResponse.errorLoadingBets,
                        };
                        if (options.typeFilter !== BetslipListType.Settled) {
                            this.summaryService.update(betslipResponse.summary);
                        }

                        return response;
                    }),
                ),
            ),
            catchError(() => {
                const response = myBetsFilteredErrorResponse();
                response.summary = this.summaryService.summary;

                return of(response);
            }),
        );
    }

    getAllSettledMyBets(options: IMyBetsListRequest): Observable<MyBetsListSlimResponse> {
        return this.api.getAllBetslips(options).pipe(
            map((betslipResponse) => betslipResponse ?? myBetsListSlimErrorResponse()),
            switchMap((betslipResponse) => {
                return this.fixtureData.fetch(betslipResponse.betslips, true).pipe(
                    catchError(() => of({})),
                    map((events) => ({
                        betslips: betslipResponse.betslips,
                        events,
                    })),
                );
            }),
            catchError(() => of(myBetsListSlimErrorResponse())),
        );
    }

    getGroupedEventIds(sourceEventIds: string[]): string[] {
        const summary = this.summaryService.summary;

        return flatten(
            sourceEventIds.map((sid) => {
                const openEvent = summary.openEvents[sid];
                if (openEvent) {
                    const ids = [sid, ...openEvent.siblings];

                    return ids;
                } else {
                    return [sid];
                }
            }),
        );
    }

    trackOpenBetsDetails(openBetCount: number) {
        this.trackingService.track(trackingConstants.EVENT_CONTENT_VIEW, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'navigation',
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'my bets',
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'load',
            [trackingConstants.COMPONENT_POSITION_EVENT]: 'not applicable',
            [trackingConstants.COMPONENT_LOCATION_EVENT]: 'open bets',
            [trackingConstants.COMPONENT_EVENT_DETAILS]: openBetCount.toString(),
            [trackingConstants.COMPONENT_URL_CLICKED]: 'not applicable',
        });
    }
}
